import { createContext, useContext, useState, useCallback, type ReactNode, type FC } from 'react';

interface ModalStackContextType {
  registerModal: (id: string, onClose: () => void) => number;
  unregisterModal: (id: string) => void;
  getTopModalId: () => string | null;
}

const ModalStackContext = createContext<ModalStackContextType | null>(null);

export const ModalStackProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [modalStack, setModalStack] = useState<Array<{ id: string; onClose: () => void; zIndex: number }>>([]);

  const registerModal = useCallback((id: string, onClose: () => void) => {
    const zIndex = 1000 + modalStack.length * 10;
    setModalStack((prev) => [...prev, { id, onClose, zIndex }]);
    return zIndex;
  }, [modalStack.length]);

  const unregisterModal = useCallback((id: string) => {
    setModalStack((prev) => prev.filter((modal) => modal.id !== id));
  }, []);

  const getTopModalId = useCallback(() => {
    if (modalStack.length === 0) return null;
    return modalStack[modalStack.length - 1].id;
  }, [modalStack]);

  return (
    <ModalStackContext.Provider value={{ registerModal, unregisterModal, getTopModalId }}>
      {children}
    </ModalStackContext.Provider>
  );
};

export const useModalStack = () => {
  const context = useContext(ModalStackContext);
  if (!context) {
    // Si pas de provider, on retourne des fonctions par défaut
    return {
      registerModal: () => 1000,
      unregisterModal: () => {},
      getTopModalId: () => null,
    };
  }
  return context;
};
